/**
 * PageHeader — per-page title block.
 *
 * NOT the app-chrome with logo (that's `<AppHeader>` in
 * `src/components/organisms`). This component sits INSIDE the content
 * area at the top of every list/index page and pairs:
 *
 *   - `title` — what this page is about (h1).
 *   - `subtitle` — optional secondary text below the title (small).
 *   - `actions` — controls and CTAs related to the page concept
 *     (group-by select, search, primary action). They live in a Row on
 *     the right side, reflowing under the title on narrow viewports.
 *
 * Used on LIST pages (e.g. «Проекты»). Detail pages of a specific
 * entity use `<EntityHeader>` instead — it carries identity (icon,
 * name, category) rather than a category label.
 *
 * Geometry: title row reflows from `row` to `column` at narrow widths so
 * actions slide under the title instead of compressing.
 *
 * @format
 */

import {forwardRef, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text} from 'ui/theme/typography';
import {Row} from 'ui/components/layout/Row';

export interface PageHeaderProps {
  /** Primary heading. Rendered as `<h1>`. */
  title: ReactNode;
  /** Optional secondary line. Rendered as a muted `body.m`. */
  subtitle?: ReactNode;
  /** Right-side controls: search, filters, CTAs. Pass any node. */
  actions?: ReactNode;
  className?: string;
}

const Title = styled('h1')(({theme}) => ({
  ...text('heading.l'),
  color: theme.palette.surface.onSurface,
  margin: 0,
  minWidth: 0,
}));

const Subtitle = styled('p')(({theme}) => ({
  ...text('body.m'),
  color: theme.palette.surface.onSurfaceMuted,
  margin: 0,
  marginTop: theme.spacing(1),
}));

const TitleColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  flex: '1 1 auto',
});

const ActionsSlot = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
  minWidth: 0,
});

export const PageHeader = forwardRef<HTMLElement, PageHeaderProps>(function PageHeader(
  {title, subtitle, actions, className},
  ref,
) {
  return (
    <Row
      ref={ref as React.Ref<HTMLDivElement>}
      as='header'
      gap='m'
      alignY='center'
      justify='between'
      reflowAt='md'
      className={className}
    >
      <TitleColumn>
        <Title>{title}</Title>
        {subtitle != null && <Subtitle>{subtitle}</Subtitle>}
      </TitleColumn>
      {actions != null && <ActionsSlot>{actions}</ActionsSlot>}
    </Row>
  );
});
